// @ flow
import React, {useEffect, useState, useRef} from "react";
import {useHistory} from "react-router-dom";
import {Descriptions, Row, Skeleton, Col, Tooltip} from "antd";
import {
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    LockOutlined,
    SafetyCertificateFilled
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {userDetailPropType} from "./userDetailPropTypes";
import {formatPhoneNumber, isConstantExist, getConstant} from "../../../utils/helpers";
import {userTypes} from "../../../constants/constants";
import {ConditionalTooltip} from "../../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {ChangePasswordModal} from "../../_defaultComponents/ChangePasswordModal/ChangePasswordModal";

export const UserDetail = ({userId}: userDetailPropType) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation(["userDetail", "userTypes"]);
    const [recall, setRecall] = useState(true);
    const [companies, setCompanies] = useState([{name: "", _id: ""}]);
    const [subCompanies, setSubCompanies] = useState([{name: "", _id: ""}]);
    const [visibleChangePasswordModal, setVisibleChangePasswordModal] = useState(false);

    const recallRef = useRef(recall);
    recallRef.current = recall;

    const [fieldManagerCompanies, setFieldManagerCompanies] = useState("");
    const [fieldManagerSubCompanies, setFieldManagerSubCompanies] = useState("");
    const [loading, userData, setData, setLoading] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId
        }),
        dependencies: [recall]
    });

    const getAccessibleFields = async userId => {
        const result = await stores.userStore.getAccessibleFields({
            userId
        });

        if (result) {
            setFieldManagerCompanies(result.data.companies?.map(c => c.companyName).join(", "));
            setFieldManagerSubCompanies(result.data.subcompanies?.map(c => c.name).join(", "));
        }
    };

    useEffect(() => {
        if (userData && userData?.data && userData?.data?.role === "fieldManager") {
            getAccessibleFields(userId);
        }
    }, [userData]);

    useEffect(() => {
        if (userData.status && userData.status !== 200) {
            stores.appStore.openErrorModal({
                content: userData.data.message,
                onOk: () => {
                    history.push("/users");
                }
            });
        } else {
            setLoading(true);
            setCompanies(
                userData?.data?.companies
                    ? userData?.data?.companies.map(c => ({
                          ...c,
                          name: c.companyName
                      }))
                    : []
            );
            setSubCompanies(userData?.data?.subcompanies ? userData?.data?.subcompanies : []);
            setLoading(false);
        }
    }, [userData]);

    const updateUserDisable = () => {
        const {disabled} = userData.data;
        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: t("userDetail.modal.updateUserDisable.content", {
                ns: "userDetail",
                disabledStatus: disabled
                    ? t("other.activeLowercase", {ns: "common"})
                    : t("other.inactiveLowercase", {ns: "common"})
            }),
            onOk: async () => {
                setLoading(true);
                let updatedUser = await stores.userStore.updateUser({
                    user: {...userData.data, disabled: !disabled},
                    disableUpdate: true
                });

                if (updatedUser) {
                    setRecall(!recallRef.current);
                }

                setLoading(false);
            }
        });
    };
    const updateUserPassword = async values => {
        const {newPassword} = values;
        await setRecall(!recallRef.current);
        setVisibleChangePasswordModal(!visibleChangePasswordModal);
        await stores.userStore.changeUserPassword({
            userId,
            password: newPassword
        });
    };

    const renderReferenceInfo = ({reference}) => {
        if (!reference) return "-";

        return (
            <>
                <p>
                    {t("fields.username", {ns: "common"})} :{" "}
                    <Link
                        to={{pathname: `/users/${reference?.panelUserId?.toString()}`}}
                        target="_blank"
                    >
                        {reference?.username}
                    </Link>
                </p>
                <p>
                    {t("fields.fullname", {ns: "common"})} : {reference?.fullName}
                </p>
                <p>
                    {t("fields.email", {ns: "common"})} : {reference?.email}
                </p>
                <p>
                    {t("fields.phoneNumber", {ns: "common"})} :{" "}
                    {formatPhoneNumber(reference?.phone)}
                </p>
                <p>Kod : {reference?.code}</p>
            </>
        );
    };

    return (
        <div data-testid="userDetailScreen">
            {loading || !userData.data ? (
                <Skeleton paragraph={{rows: 2, width: "100%"}}></Skeleton>
            ) : (
                <>
                    <ChangePasswordModal
                        title={t("userDetail.modal.userResetPasswordModalTitle", {
                            ns: "userDetail"
                        })}
                        isVisible={visibleChangePasswordModal}
                        updateCourierPassword={values => updateUserPassword(values)}
                        setVisible={() => {
                            setVisibleChangePasswordModal(!visibleChangePasswordModal);
                        }}
                    />
                    <Row gutter={[0, 20]}>
                        <Col span={24}>
                            <Descriptions
                                title={
                                    <Row type="flex" justify="space-between">
                                        {userData.data.fullName}
                                        <ConditionalTooltip
                                            text={t("userDetail.disabledUpdateUserTooltip", {
                                                ns: "userDetail"
                                            })}
                                            condition={userData.data.disabled}
                                            child={
                                                <div>
                                                    <Tooltip
                                                        title={t("userDetail.authorization", {
                                                            ns: "userDetail"
                                                        })}
                                                    >
                                                        <CustomButton
                                                            type="primary"
                                                            data-testid="authorizationButton"
                                                            href={"/users/authorization/" + userId}
                                                            disabled={userData.data.disabled}
                                                            onClick={event => {
                                                                event.preventDefault();
                                                                history.push(
                                                                    "/users/authorization/" + userId
                                                                );
                                                            }}
                                                            icon={<SafetyCertificateFilled />}
                                                        />
                                                    </Tooltip>
                                                    {["cUser", "sOperation"].includes(
                                                        userData?.data?.role
                                                    ) && (
                                                        <CustomButton
                                                            type="primary"
                                                            disabled={userData.data.disabled}
                                                            onClick={() =>
                                                                setVisibleChangePasswordModal(true)
                                                            }
                                                            icon={<LockOutlined />}
                                                        />
                                                    )}
                                                    <CustomButton
                                                        type="primary"
                                                        data-testid="editButton"
                                                        href={"/users/edit/" + userId}
                                                        disabled={userData.data.disabled}
                                                        onClick={event => {
                                                            event.preventDefault();
                                                            history.push("/users/edit/" + userId);
                                                        }}
                                                        icon={<EditOutlined />}
                                                    />
                                                </div>
                                            }
                                        />
                                    </Row>
                                }
                                column={1}
                                bordered
                            >
                                <Descriptions.Item label={t("fields.username", {ns: "common"})}>
                                    <span data-testid="username">{userData.data.username}</span>
                                </Descriptions.Item>
                                <Descriptions.Item label="E-mail">
                                    <span data-testid="email">{userData.data.email}</span>
                                </Descriptions.Item>
                                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                                    <Descriptions.Item label={t("fields.userRole", {ns: "common"})}>
                                        <span data-testid="role">
                                            {userData.data.role &&
                                                (isConstantExist(userTypes, userData.data.role)
                                                    ? t(
                                                          getConstant(userTypes, userData.data.role)
                                                              .translation_key,
                                                          {
                                                              ns: "userTypes"
                                                          }
                                                      )
                                                    : "-")}
                                        </span>
                                    </Descriptions.Item>
                                )}

                                {["cUser", "oUser", "sOperation"].includes(userData.data.role) && (
                                    <>
                                        <Descriptions.Item
                                            label={t("fields.company", {ns: "common"})}
                                        >
                                            <span data-testid="company">
                                                {userData.data.auth?.companies.length > 0
                                                    ? companies
                                                          .map(company => company.name)
                                                          .join(", ")
                                                    : t("userDetail.noCompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.subcompany", {ns: "common"})}
                                        >
                                            <span data-testid="subcompany">
                                                {userData.data.auth?.subcompanies.length > 0
                                                    ? subCompanies
                                                          .map(company => company.name)
                                                          .join(", ")
                                                    : t("userDetail.noSubcompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                    </>
                                )}
                                {["fieldManager"].includes(userData.data.role) && (
                                    <>
                                        <Descriptions.Item
                                            label={t("fields.company", {ns: "common"})}
                                        >
                                            <span data-testid="company">
                                                {fieldManagerCompanies !== ""
                                                    ? fieldManagerCompanies
                                                    : t("userDetail.noCompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                        <Descriptions.Item
                                            label={t("fields.subcompany", {ns: "common"})}
                                        >
                                            <span data-testid="subcompany">
                                                {fieldManagerSubCompanies !== ""
                                                    ? fieldManagerSubCompanies
                                                    : t("userDetail.noSubcompanyAdded", {
                                                          ns: "userDetail"
                                                      })}
                                            </span>
                                        </Descriptions.Item>
                                    </>
                                )}

                                <>
                                    {["cUser", "sOperation"].includes(userData.data.role) && (
                                        <Descriptions.Item
                                            label={t("fields.title", {ns: "common"})}
                                        >
                                            <span data-testid="title">{userData.data.title}</span>
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item
                                        label={t("fields.phoneNumber", {ns: "common"})}
                                    >
                                        <span data-testid="phoneNumber">
                                            {formatPhoneNumber(userData.data.phone)}
                                        </span>
                                    </Descriptions.Item>
                                </>

                                <Descriptions.Item
                                    label={t("userDetail.fields.userActivity", {ns: "userDetail"})}
                                >
                                    <span data-tesid="userActivity">
                                        {userData.data.disabled
                                            ? t("other.inactive", {ns: "common"})
                                            : t("other.active", {ns: "common"})}
                                    </span>
                                </Descriptions.Item>
                                {stores.meStore.checkRole("isAdmin") && (
                                    <Descriptions.Item label={"Uat Bilgisi"}>
                                        <span data-testid="userUat">
                                            {userData?.data?.uat
                                                ? t("other.active", {ns: "common"})
                                                : "Pasif"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                                <Descriptions.Item label={"Test Kullanıcı"}>
                                    <span data-testid="testUser">
                                        {userData?.data?.isTest
                                            ? t("other.yes", {ns: "common"})
                                            : t("other.no", {ns: "common"})}
                                    </span>
                                </Descriptions.Item>
                                {stores.meStore.checkRole("isAdmin") && (
                                    <Descriptions.Item
                                        label={"Referans"}
                                        contentStyle={{maxWidth: "200px"}}
                                    >
                                        <span data-testid="reference">
                                            {userData?.data?.reference
                                                ? renderReferenceInfo({
                                                      reference: userData?.data?.reference
                                                  })
                                                : "-"}
                                        </span>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Col>
                    </Row>
                    <Row type="flex" justify="end">
                        {stores.meStore.username !== userData.data.username && (
                            <CustomButton
                                type={"primary"}
                                danger
                                data-testid="activeInActiveButton"
                                onClick={() => {
                                    updateUserDisable(userData.data);
                                }}
                                icon={
                                    userData.data.disabled ? <CheckOutlined /> : <CloseOutlined />
                                }
                            >
                                {userData.data.disabled
                                    ? t("actions.activate", {ns: "common"})
                                    : t("actions.inactivate", {ns: "common"})}
                            </CustomButton>
                        )}
                    </Row>
                </>
            )}
        </div>
    );
};
